// `react-native` on web: react-native-web plus the pieces it leaves out or stubs.
export * from 'react-native-web';
import { createElement, forwardRef } from 'react';
import {
  Alert as RNWAlert,
  Image as RNWImage,
  ImageBackground as RNWImageBackground,
  Keyboard as RNWKeyboard,
  StyleSheet as RNWStyleSheet,
} from 'react-native-web';

type AlertButton = { text?: string; onPress?: (value?: string) => void; style?: 'default' | 'cancel' | 'destructive' };

// react-native-web's Alert.alert is a no-op; fall back to the browser dialogs.
// TODO(pwa): replace with an in-app dialog that matches the app's design.
export const Alert = {
  ...RNWAlert,
  alert(title: string, message?: string, buttons?: AlertButton[]) {
    const text = [title, message].filter(Boolean).join('\n\n');
    if (!buttons || buttons.length <= 1) {
      window.alert(text);
      buttons?.[0]?.onPress?.();
      return;
    }
    const cancel = buttons.find(b => b.style === 'cancel');
    const action = buttons.filter(b => b !== cancel).pop() ?? buttons[buttons.length - 1];
    if (window.confirm(text)) action.onPress?.();
    else cancel?.onPress?.();
  },
  prompt(title: string, message?: string, callback?: (text: string) => void) {
    const value = window.prompt([title, message].filter(Boolean).join('\n\n'));
    if (value !== null) callback?.(value);
  },
};

export const ToastAndroid = {
  SHORT: 0,
  LONG: 1,
  show() {},
  showWithGravity() {},
};

export const PermissionsAndroid = {
  PERMISSIONS: {} as Record<string, string>,
  RESULTS: { GRANTED: 'granted', DENIED: 'denied', NEVER_ASK_AGAIN: 'never_ask_again' },
  async check() {
    return false;
  },
  async request() {
    return 'denied';
  },
};

export const ActionSheetIOS = {
  showActionSheetWithOptions() {},
  showShareActionSheetWithOptions() {},
};

type AssetSource = { uri: string; width?: number; height?: number } | string | number | null | undefined;

// RNW's Image has no resolveAssetSource; local assets are already `{ uri, width, height }`
// objects (see web/vite-plugin-rn-assets.ts).
export const Image = Object.assign(RNWImage, {
  resolveAssetSource(source: AssetSource) {
    if (source == null || typeof source === 'number') return null;
    return typeof source === 'string' ? { uri: source } : source;
  },
});

// RNW's ImageBackground hands the box's own width and height to the inner <Image>, and an Image without them
// takes the picture's intrinsic size: in a box sized by `width: '100%'` alone the picture kept its natural
// height (540 px for the card hero on Card details), so `cover` blew it up and showed a corner of the card.
// The picture fills the box instead, as on native.
type ImageBackgroundProps = { style?: unknown; imageStyle?: unknown; [key: string]: unknown };
export const ImageBackground = forwardRef<unknown, ImageBackgroundProps>((props, ref) => {
  const { width, height } = (RNWStyleSheet.flatten(props.style as never) ?? {}) as { width?: unknown; height?: unknown };
  return createElement(RNWImageBackground as never, {
    ...props,
    ref,
    imageStyle: [{ width: width ?? '100%', height: height ?? '100%' }, props.imageStyle],
  });
});

// Screens wrap their content in `<TouchableWithoutFeedback onPress={Keyboard.dismiss}>` to hide the
// keyboard on a tap outside a field. On web that press also fires for a click *on* the field, and
// RNW's dismiss blurs it — focus is lost the moment the mouse is released. Ignore dismiss when the
// pointer went down on the focused field itself.
let lastPointerDown: EventTarget | null = null;
if (typeof document !== 'undefined') {
  document.addEventListener('pointerdown', e => (lastPointerDown = e.target), true);
}

export const Keyboard = Object.assign(RNWKeyboard, {
  dismiss() {
    const active = document.activeElement as HTMLElement | null;
    const isField = !!active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable);
    if (isField && lastPointerDown instanceof Node && active.contains(lastPointerDown)) return;
    active?.blur?.();
  },
});
